import React, { useState, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
	Dropdown,
	Grid,
	Input,
	Menu,
	Message,
	Modal,
	Spin,
	Button as ArcoButton,
} from '@arco-design/web-react';
import {
	IconPlus,
	IconEye,
	IconDelete,
	IconAt,
	IconArrowLeft,
	IconCaretDown,
} from '@arco-design/web-react/icon';
import styles from './index.module.scss';
import { Uploader, UploaderServer } from '@extensions/AttributePanel/utils/Uploader';
import { classnames } from '@extensions/AttributePanel/utils/classnames';
import { previewLoadImage } from '@extensions/AttributePanel/utils/previewLoadImage';
import { Link } from '@extensions/AttributePanel/components/attributes/Link';
import { MergeTags } from '@extensions';
import { useEditorProps } from '@bay-email/editor';
import { MergeTagContext } from '@extensions/utils/getMergeTagsForContext';
import BayToast from '@bay-ui/BayToast';
import BayTooltip from '@bay-ui/BayTooltip';
import BaySpinner from '@bay-ui/BaySpinner';
import BayDropdown from '@bay-ui/BayDropdown';
import BayFilesUpload from '@bay-ui/BayFilesUpload';
import BayFilesProgressModal from '@bay-ui/BayFilesProgressModal';
import {
	getAllowedFileTypes,
	defaultAllowedImageTypes,
} from '@bay-ui/utilities/constants/mimeTypes';
import { Button } from 'react-bootstrap';
import { useDropzone, FileRejection } from 'react-dropzone';
import {
	BsCrop,
	BsTrash3,
	BsArrowRepeat,
	BsBoxArrowInDownLeft,
	BsGear,
	BsCaretDownFill,
	BsUpload,
} from 'react-icons/bs';
import { t, getMergeTagImageUrl, isMergeTagUrl } from '@bay-email/core';
import BayImageCropModal from '@bay-ui/BayImageCropModal';
import type { BayImageCropModalHandle } from '@bay-ui/BayImageCropModal';
import { constructTemporaryUrl } from './imageUploadAssets';

// NOTE: The API utility is imported to handle image deletion in the handleDeleteImage function.
// It calls the deleteImage method from the entries API, allowing the component to send a
// request to the backend to delete the image when the user confirms the deletion.

// TODO: In the future, we need to implement a common service for other platforms to call
// the delete image API. Currently, this implementation supports only DSA.
let apiPromise: Promise<any> | null = null;

try {
	apiPromise = import('~/services')
		.then(mod => mod.default)
		.catch(() => {
			console.warn('API module not available');
			return null;
		});
} catch {
	apiPromise = Promise.resolve(null);
}

// Import FILESTORE_API_URL from environment variables for use in the BayFilesProgressModal API configuration
import { FILESTORE_API_URL } from '~/lib/environment';
export interface ImageUploaderProps {
	onChange: (val: string) => void;
	value: string;
	label?: string;
	uploadHandler?: UploaderServer;
	autoCompleteOptions?: Array<{ value: string; label: React.ReactNode }>;
	context?: MergeTagContext;
	isImageCropperAllowed?: boolean;
	showLink?: boolean;
	isDynamicImageAllowed?: boolean;
	headerTitle?: string;
	/**
	 * Fires after a new image URL is committed, saying how it got here.
	 *
	 * 'upload' — a fresh image arrived (drop, file picker, paste, Import URL),
	 *   including the crop step embedded in that flow.
	 * 'crop'   — the user re-opened the Cropper on the image already in the
	 *   field and saved.
	 *
	 * Callers size dynamic image fields differently for the two: an upload only
	 * seeds dimensions that were never set, a standalone crop always reseeds.
	 */
	onImageCommitted?: (_reason: 'upload' | 'crop') => void;
	/**
	 * Grey out every way of getting an image in — button, dropdown caret and
	 * drag-and-drop. Used while a dynamic image field has no source selected,
	 * since there is nothing for a fallback to fall back to yet.
	 */
	uploadDisabled?: boolean;
}

function inferImageMimeTypeFromValue(value?: string): 'image/png' | 'image/jpeg' {
	if (!value) return 'image/jpeg';

	const normalizedValue = value.split('?')[0].split('#')[0].toLowerCase();

	if (normalizedValue.endsWith('.png')) return 'image/png';
	if (normalizedValue.endsWith('.jpg') || normalizedValue.endsWith('.jpeg'))
		return 'image/jpeg';

	return 'image/jpeg';
}

function withCacheBuster(url: string): string {
	if (!url) return url;
	const separator = url.includes('?') ? '&' : '?';
	return `${url}${separator}cb=${Date.now()}`;
}

export function ImageUploader(props: ImageUploaderProps) {
	const toastDefaultData = { showToast: false, message: '', variant: 'success' };
	const isCropperAllowed = props.isImageCropperAllowed || false;
	const isMergeTagsAllowed = isCropperAllowed && props.value && props.showLink;
	const isDynamicImageAllowed = props.isDynamicImageAllowed || false;
	const uploadDisabled = props.uploadDisabled || false;
	const { mergeTags } = useEditorProps();
	const processedUrl = isDynamicImageAllowed
		? getMergeTagImageUrl(props.value, 'testing')
		: props.value;
	const isMergeTag = isMergeTagUrl(props.value);

	const cropperModalRef = useRef<BayImageCropModalHandle | null>(null);
	const uploadHandlerRef = useRef<UploaderServer | null | undefined>(props.uploadHandler);

	const [isUploading, setIsUploading] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [selectedImgUrl, setSelectedImgUrl] = useState('');
	const [validImageUrl, setValidImageUrl] = useState(true);
	const [toastDetails, setToastDetails] = useState(toastDefaultData);
	const [modalType, setModalType] = useState<
		null | 'crop' | 'import' | 'delete' | 'preview'
	>(null);
	const [showUploadFileModal, setShowUploadFileModal] = useState(false);
	const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);
	const [imageName, setImageName] = useState('');
	const [deleteImageID, setDeleteImageID] = useState<any>(null);
	const [cropOutputMimeType, setCropOutputMimeType] = useState<
		'image/png' | 'image/jpeg'
	>('image/jpeg');

	// Why the Cropper is open: reopened on the existing image ('crop'), or the
	// crop step of bringing a new one in ('upload'). Only the former reseeds a
	// dynamic field's dimensions.
	const commitReasonRef = useRef<'upload' | 'crop'>('upload');

	const onChange = useCallback(
		(val: string) => {
			props.onChange(val);
		},
		[props],
	);

	const commitImage = useCallback(
		(val: string, reason: 'upload' | 'crop') => {
			props.onChange(val);
			props.onImageCommitted?.(reason);
		},
		[props],
	);

	const onUpload = useCallback(() => {
		if (isUploading) {
			return Message.warning(t('Uploading...'));
		}
		if (!uploadHandlerRef.current) return;

		const uploader = new Uploader(uploadHandlerRef.current, {
			limit: 1,
			accept: 'image/*',
		});

		uploader.on('start', photos => {
			setIsUploading(true);

			uploader.on('end', data => {
				const url = data[0]?.url;
				if (url) {
					commitImage(url, 'upload');
				} else {
					console.error('❌ ImageUploader: No URL in upload result!', data);
				}
				setIsUploading(false);
			});
		});

		uploader.chooseFile();
	}, [commitImage, isUploading]);

	const onPaste = useCallback(
		async (e: React.ClipboardEvent<HTMLInputElement>) => {
			if (!uploadHandlerRef.current) return;
			const clipboardData = e.clipboardData;

			for (let i = 0; i < clipboardData.items.length; i++) {
				const item = clipboardData.items[i];
				if (item.kind == 'file') {
					const blob = item.getAsFile();

					if (!blob || blob.size === 0) {
						return;
					}
					try {
						setIsUploading(true);
						const picture = await uploadHandlerRef.current(blob);
						await previewLoadImage(picture);
						commitImage(picture, 'upload');
						setIsUploading(false);
					} catch (error: any) {
						Message.error(error?.message || error || t('Upload failed'));
						setIsUploading(false);
					}
				}
			}
		},
		[commitImage, props],
	);

	const onRemove = useCallback(() => {
		props.onChange(props.value || '');
	}, [props]);

	const handleDrop = useCallback(
		async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
			// Handle rejected files
			if (rejectedFiles.length) {
				const [{ errors = [] } = {}] = rejectedFiles;
				const [{ code } = {}] = errors;

				const errorMessages: Record<string, string> = {
					'file-too-large': 'File must be below 15MB',
					'file-invalid-type': 'Invalid file type',
					'too-many-files': 'Only one file is allowed',
				};

				const message =
					code && errorMessages[code] ? errorMessages[code] : 'File upload failed';

				setToastDetails({
					showToast: true,
					message,
					variant: 'danger',
				});

				return; // stop execution if rejected
			}

			if (!acceptedFiles?.length) return;

			const file = acceptedFiles[0];
			setImageName(file.name);
			setCropOutputMimeType(file.type === 'image/png' ? 'image/png' : 'image/jpeg');
			const imageUrl = URL.createObjectURL(file);
			setSelectedImgUrl(imageUrl);
			// Crop step of bringing in a NEW image, not a standalone re-crop.
			commitReasonRef.current = 'upload';
			setModalType('crop');
		},
		[],
	);

	function checkImage(url: string): Promise<boolean> {
		return new Promise(resolve => {
			const img = new Image();
			img.onload = () => resolve(true);
			img.onerror = () => resolve(false);
			img.src = url;
		});
	}

	// Handle cropped blob from the new ImageCropperModal
	const handleCroppedBlob = async (blob: Blob, mimeType: 'image/png' | 'image/jpeg') => {
		const normalizedMimeType = mimeType || cropOutputMimeType;
		const extension = normalizedMimeType === 'image/png' ? 'png' : 'jpg';
		const baseFileName = imageName ? imageName.replace(/\.[^.]+$/, '') : 'cropped-image';

		const file = new File([blob], `${baseFileName}.${extension}`, {
			type: normalizedMimeType,
		});

		if (uploadHandlerRef.current) {
			try {
				setIsUploading(true);
				const picture = await uploadHandlerRef.current(file);
				await previewLoadImage(picture);
				commitImage(picture, commitReasonRef.current);
				setToastDetails({
					showToast: true,
					message: t('Image uploaded successfully!'),
					variant: 'success',
				});
			} catch (error: unknown) {
				const message =
					error instanceof Error ? error.message : String(error || t('Upload failed'));
				Message.error(message);
				setToastDetails({
					showToast: true,
					message: t('Image Upload failed'),
					variant: 'danger',
				});
			} finally {
				setIsUploading(false);
				setModalType(null);
			}
			return;
		}

		setUploadingFiles([file]);
		setShowUploadFileModal(true);
		setModalType(null);
	};

	// Trigger the crop save when user clicks Save button
	const handleCropAndUpload = async () => {
		if (cropperModalRef.current) {
			await cropperModalRef.current.triggerSave();
		}
	};

	const handleOnChange = async (val: string) => {
		let isValid = true;

		if (val) {
			isValid = await checkImage(val);
		}

		setValidImageUrl(isValid);
		setCropOutputMimeType(inferImageMimeTypeFromValue(val));
		setImageName('imported-image.jpg');
		setSelectedImgUrl(val);
	};

	const handleModalClose = () => {
		setModalType(null);
		setSelectedImgUrl('');
		setValidImageUrl(true);
		onRemove();
		setImageName('');
	};

	const handleCropImage = () => {
		// Re-cropping what is already in the field: this one reseeds dimensions.
		commitReasonRef.current = 'crop';
		setModalType('crop');
		setSelectedImgUrl(withCacheBuster(props.value));
		setCropOutputMimeType(inferImageMimeTypeFromValue(props.value));
	};

	const handleProgessModalClose = () => {
		setShowUploadFileModal(false);
		setUploadingFiles([]);
	};

	const { getRootProps, getInputProps, open, isDragAccept, isDragReject } = useDropzone({
		accept: {
			'image/jpeg': ['.jpg', '.jpeg'],
			'image/png': ['.png'],
		},
		multiple: false,
		noClick: true,
		noKeyboard: true,
		maxSize: 15 * 1024 * 1024,
		onDrop: handleDrop,
	});

	const handleDeleteImage = async () => {
		setIsDeleting(true);
		try {
			const api = await apiPromise;

			if (deleteImageID && api?.entries?.delete) {
				await api.entries.delete([deleteImageID], 'design-assets');
			}
			onChange('');
			setSelectedImgUrl('');
			setToastDetails({
				showToast: true,
				message: t('Image removed successfully!'),
				variant: 'success',
			});
			setDeleteImageID('');
		} catch (error: any) {
			setToastDetails({
				showToast: true,
				message: t('Failed to remove image.'),
				variant: 'danger',
			});
		} finally {
			setIsDeleting(false);
			setModalType(null);
		}
	};

	const actionItems = [
		{
			icon: (
				<BsUpload
					size={16}
					className='ms-1'
				/>
			),
			eventKey: 'upload',
			label: t('Upload from Computer'),
			onClick: () => open(),
			textcolor: 'text-dark',
		},
		{
			icon: (
				<BsBoxArrowInDownLeft
					size={16}
					className='ms-1'
				/>
			),
			eventKey: 'import',
			label: t('Import URL'),
			onClick: () => setModalType('import'),
			textcolor: 'text-dark',
		},
		...(isDynamicImageAllowed
			? [
					{
						eventKey: 'dynamicImage',
						keepMenuOpen: true,
						label: (
							<div onClick={e => e.stopPropagation()}>
								<MergeTags
									value={props.value}
									children={
										<div className='d-flex gap-2 align-items-center'>
											<BsGear size={16} />
											<span className='text-dark ms-1'>{t('Dynamic Image')}</span>
											<BsCaretDownFill size={16} />
										</div>
									}
									onChange={onChange}
									context='image-src'
								/>
							</div>
						),
					},
				]
			: []),
	];

	const content = useMemo(() => {
		if (isUploading && !isCropperAllowed) {
			return (
				<div className={styles['item']}>
					<div className={classnames(styles['info'])}>
						<Spin />
						<div className={styles['btn-wrap']} />
					</div>
				</div>
			);
		}

		if (!props.value) {
			return !isCropperAllowed ? (
				<div
					className={styles['upload']}
					onClick={onUpload}
				>
					<IconPlus />
					<div>Upload</div>
				</div>
			) : (
				<div
					className='d-flex flex-column gap-2 flex-1'
					{...(uploadDisabled ? {} : getRootProps())}
				>
					<input {...getInputProps()} />
					{/*
					  BayFilesUpload runs its own useDropzone internally, so
					  dropping the outer getRootProps above is not enough to stop a
					  drag landing here. Neutralise the whole zone instead — it
					  stays visible, greyed, and inert.
					*/}
					<div
						style={
							uploadDisabled
								? { pointerEvents: 'none', opacity: 0.5 }
								: undefined
						}
					>
					<BayFilesUpload
						allowedFileTypes={getAllowedFileTypes(defaultAllowedImageTypes).mimeTypes}
						allowedFileTypeslabel={t(
							"Drag 'n' drop image here, or select from options below",
						)}
						allowedFileTypesText={'JPG, JPEG or PNG'}
						noData={true}
						onDrop={handleDrop}
						cloudIconSize={24}
						allowedFileUpload={false}
						isMultiple={false}
						dropZoneData={{
							isAllowRejectedFile: true,
							maxSize: 15 * 1024 * 1024,
							allowedFiles: {
								'image/jpeg': ['.jpg', '.jpeg'],
								'image/png': ['.png'],
							},
						}}
					/>
					</div>
					<div
						className='d-flex'
						style={{ gap: 1 }}
					>
						<ArcoButton
							className='rounded-start-1 rounded-end-0 flex-1'
							type='primary'
							disabled={uploadDisabled}
							onClick={open}
						>
							{t('Upload Image')}
						</ArcoButton>
						<BayDropdown
							items={uploadDisabled ? [] : actionItems}
							dropdownMenuClassname='ps-2'
						>
							<ArcoButton
								className='rounded-end-1 rounded-start-0'
								type='primary'
								disabled={uploadDisabled}
								icon={<IconCaretDown />}
							/>
						</BayDropdown>
					</div>
				</div>
			);
		}

		return isCropperAllowed ? (
			<div className='w-100 d-flex flex-column gap-2 mb-2'>
				<input {...getInputProps()} />
				<img
					className='w-100 rounded'
					src={processedUrl}
					style={{
						height: 'auto',
						objectFit: 'contain',
						maxHeight: '300px',
						display: 'block',
					}}
				/>
				<div className='d-flex gap-3 mt-2'>
					<BayTooltip
						disabled={!isMergeTag}
						overlay={t('Crop is not available for dynamic images.')}
					>
						<div className='cursor-pointer'>
							<ArcoButton
								className='border-0 p-0 h-auto'
								type='outline'
								icon={<BsCrop size={16} />}
								onClick={() => handleCropImage()}
								disabled={isMergeTag}
							>
								{t('Crop Image')}
							</ArcoButton>
						</div>
					</BayTooltip>
					<div className='vr h-50 align-self-center' />
					<ArcoButton
						className='border-0 p-0 h-auto'
						type='outline'
						status='danger'
						icon={<BsTrash3 size={16} />}
						onClick={() => setModalType('delete')}
					>
						{t('Remove Image')}
					</ArcoButton>
				</div>
			</div>
		) : (
			<div className={styles['item']}>
				<div className={classnames(styles['info'])}>
					<img src={props.value} />
					<div className={styles['btn-wrap']}>
						<a
							title={t('Preview')}
							onClick={() => setModalType('preview')}
						>
							<IconEye />
						</a>
						<a
							title={t('Remove')}
							onClick={() => onChange('')}
						>
							<IconDelete />
						</a>
					</div>
				</div>
			</div>
		);
	}, [
		isUploading,
		onRemove,
		onUpload,
		props.value,
		isDragAccept,
		isDragReject,
		getRootProps,
		getInputProps,
		open,
		processedUrl,
		isMergeTag,
		uploadDisabled,
	]);

	if (!props.uploadHandler) {
		return (
			<Input
				value={props.value}
				onChange={onChange}
			/>
		);
	}

	const isImportDisabled = !(props.value || selectedImgUrl) || !validImageUrl;

	const renderFooter = () => {
		const commonWrapperClass = 'd-flex justify-content-between gap-2';

		switch (modalType) {
			case 'delete':
				return (
					<div className={commonWrapperClass}>
						<Button
							disabled={isDeleting}
							variant='outline-secondary'
							onClick={handleModalClose}
						>
							{t('Cancel')}
						</Button>
						<Button
							disabled={isDeleting}
							variant='danger'
							onClick={handleDeleteImage}
						>
							{isDeleting ? (
								<BaySpinner
									title={t('Removing Image...')}
									variant='light'
									textColor='text-white'
								/>
							) : (
								t('Remove')
							)}
						</Button>
					</div>
				);

			case 'import':
				return (
					<div className={commonWrapperClass}>
						<Button
							variant='outline-secondary'
							onClick={handleModalClose}
						>
							{t('Cancel')}
						</Button>
						<Button
							variant='primary'
							disabled={isImportDisabled}
							onClick={() => {
								// Import URL brings in a new image; its crop step is part
								// of that flow, not a standalone re-crop.
								commitReasonRef.current = 'upload';
								setModalType('crop');
							}}
						>
							{t('Import')}
						</Button>
					</div>
				);

			case 'crop':
				return (
					<div className={commonWrapperClass}>
						<Button
							className='text-decoration-none text-500'
							variant='link'
							onClick={handleModalClose}
							disabled={isUploading}
						>
							<IconArrowLeft />
							<span className='ms-2'>{t('Cancel')}</span>
						</Button>
						<Button
							disabled={isUploading}
							variant='primary'
							onClick={handleCropAndUpload}
						>
							{isUploading ? (
								<BaySpinner
									title={t('Uploading...')}
									variant='light'
									textColor='text-white'
								/>
							) : (
								t('Save & Upload')
							)}
						</Button>
					</div>
				);

			default:
				return null;
		}
	};

	const handleApiResponse = (res: any) => {
		// Store the full response data for later use (e.g., for delete)
		setDeleteImageID(res?.result?._id || '');
		const url = res?.result?.url || '';
		if (url) {
			commitImage(url, commitReasonRef.current);
			setToastDetails({
				showToast: true,
				message: t('Image uploaded successfully!'),
				variant: 'success',
			});
		} else {
			setToastDetails({
				showToast: true,
				message: t('Image Upload failed'),
				variant: 'danger  ',
			});
		}
		setShowUploadFileModal(false);
		setUploadingFiles([]);
	};

	return (
		<>
			{props.headerTitle && (
				<div className='d-flex justify-content-between align-items-center mb-2'>
					<div className='fw-bold'>{props.headerTitle}</div>
					{props.value && (
						<BayDropdown
							items={actionItems}
							dropdownMenuClassname='ps-2'
						>
							<ArcoButton
								className='border-0 p-0'
								type='outline'
							>
								<div className='d-flex gap-2 align-items-center'>
									<BsArrowRepeat size={16} />
									<span className='dynamic-field'>{t('Change Image')}</span>
									<BsCaretDownFill size={16} />
								</div>
							</ArcoButton>
						</BayDropdown>
					)}
				</div>
			)}
			<div className={styles.wrap}>
				<div className={styles['container']}>
					{content}
					{!isCropperAllowed && (
						<Grid.Row style={{ width: '100%', display: 'flex', gap: '4px' }}>
							{mergeTags && (
								<MergeTags
									value={props.value}
									onChange={onChange}
									iconOnly={true}
									context={props.context}
								/>
							)}
							<Input
								style={{ flex: 1 }}
								onPaste={onPaste}
								value={props.value}
								onChange={onChange}
								disabled={isUploading}
							/>
							{props.autoCompleteOptions && (
								<Dropdown
									position='tr'
									droplist={
										<Menu
											onClickMenuItem={indexStr => {
												if (!props.autoCompleteOptions) return;
												onChange(props.autoCompleteOptions[+indexStr]?.value);
											}}
										>
											{props.autoCompleteOptions.map((item, index) => {
												return (
													<Menu.Item
														style={{ display: 'flex', alignItems: 'center' }}
														key={index.toString()}
													>
														<img
															src={item.value}
															style={{ width: 20, height: 20 }}
														/>
														&emsp;<span>{item.label}</span>
													</Menu.Item>
												);
											})}
										</Menu>
									}
								>
									<ArcoButton icon={<IconAt />} />
								</Dropdown>
							)}
						</Grid.Row>
					)}
					{isMergeTagsAllowed && (
						<Link
							context='image-link'
							isCropperAllowed={true}
						/>
					)}
				</div>

				<Modal
					visible={!!modalType && modalType !== 'crop'}
					onCancel={handleModalClose}
					maskClosable={true}
					className='rounded'
					style={
						modalType === 'import'
							? { width: '90vw', maxWidth: 800, maxHeight: '90vh' }
							: undefined
					}
					title={
						modalType === 'import' ? (
							<div className='text-start fs-5'>{t('Import URL')}</div>
						) : undefined
					}
					footer={renderFooter}
				>
					{/* DELETE IMAGE */}
					{modalType === 'delete' && (
						<div className='d-flex flex-column gap-3'>
							<div className='fw-bold fs-5'>{t('Remove Image')}</div>
							<div>{t('Are you sure you want to remove this image?')}</div>
						</div>
					)}

					{/* IMPORT IMAGE URL */}
					{modalType === 'import' && (
						<>
							<p className='mb-3'>{t('Enter the URL of an image from the web.')}</p>
							<label className='mb-2 pb-1 fw-bold'>
								<span>{t('Image URL')}</span>
								<span className='text-red'>*</span>
							</label>
							<Input
								className='rounded-1 flex-1'
								onPaste={onPaste}
								disabled={isUploading}
								onChange={handleOnChange}
								value={props.value || selectedImgUrl}
								placeholder='https://example.com/image.jpg'
								status={!validImageUrl ? 'error' : undefined}
							/>

							{!validImageUrl && (
								<div className='text-red mt-2'>
									{t(
										'The image URL provided is invalid. Please check the link and try again.',
									)}
								</div>
							)}
						</>
					)}

					{/* PREVIEW IMAGE */}
					{modalType === 'preview' && (
						<img
							alt={t('Preview')}
							className='w-100'
							src={props.value}
						/>
					)}
				</Modal>

				<BayImageCropModal
					show={modalType === 'crop'}
					onHide={handleModalClose}
					ref={cropperModalRef}
					imageURL={selectedImgUrl}
					outputMimeType={cropOutputMimeType}
					t={t}
					isUploading={isUploading}
					onCropComplete={handleCroppedBlob}
					layout='wide'
					controlPlacement='bottom'
					showCropShapeControl={false}
					showCropShapeControlOptions={true}
					cropContainerStyle={{ minHeight: '350px' }}
					cropContainerClassName='mb-4'
					showRotationTickMarks={false}
					showScaleTickMarks={false}
					showScaleRangeLabels={false}
					sliderLayout='stacked'
					isSplitRotationView={true}
				/>

				{showUploadFileModal && (
					<BayFilesProgressModal
						allowedFileTypes={getAllowedFileTypes(defaultAllowedImageTypes).mimeTypes}
						allowedFileTypeslabel={t(
							"Drag 'n' drop image here, or select from options below",
						)}
						allowedFileTypesText={'JPG, JPEG or PNG'}
						apiConfig={{
							requestUrl: `${FILESTORE_API_URL}/partitions/design-assets/entries$File`,
							method: 'POST',
							params: {
								path: constructTemporaryUrl(uploadingFiles[0] || {}),
								onConflict: 'rename',
								editor_type: 'email',
								// Backend supports overriding image output shape via transforms.image.format.
								// Keep png for transparency; default jpeg for non-png uploads.
								'transforms.image.format': JSON.stringify({
									type: uploadingFiles[0]?.type === 'image/png' ? 'png' : 'jpeg',
								}),
							},
							withCredentials: true,
						}}
						handleModalClose={handleProgessModalClose}
						maxFileSize={10 * 1024 * 1024}
						maxFileSizeLabel={100}
						showUploadingFilesModal={showUploadFileModal}
						t={t}
						uploadingFiles={uploadingFiles}
						cloudIconSize={24}
						allowedFileUpload={false}
						setApiRes={(res: any) => handleApiResponse(res)}
						isImageUploader={true}
					/>
				)}

				{createPortal(
					<BayToast
						show={toastDetails.showToast}
						onClose={() => setToastDetails({ ...toastDetails, showToast: false })}
						text={toastDetails.message}
						variant={toastDetails.variant}
						position='top-end'
					/>,
					document.body,
				)}
			</div>
		</>
	);
}
